import React from 'react';
import { useAppDispatch } from "../../../../../../../../app/hooks";
import {
    FieldOptionDTO,
    ActionType,
    UpdateBotToolSettingsRequest,
    SubtractionType
} from "../../../../../../../../api/generated";
import {
    updateRuleSection,
    deleteRuleSection,
    addConditionGroup,
    UpdateRuleSectionRequestUI
} from "../../../toolSettingsSlice";
import { SelectDropdown, SelectOption } from "../../../../../../../elements/select/SelectDropdown";
import { DeleteButton } from "../../../../../../../elements/deleteButton/DeleteButton";
import { CustomSwitchField } from "../../../../../../../elements/customSwitchField/CustomSwitchField";
import { ConditionGroup } from "./ConditionGroup";
import { ValidationResult } from "../../../../../../../elements/validator/component/ValidationComponent";
import { SubtractInputField } from "../../../../../../../elements/subtractInputField/SubtractInputField";
import styles from "./RuleSection.module.css";

interface RuleSectionProps {
    section: UpdateRuleSectionRequestUI;
    sectionIndex: number;
    fieldOptions: FieldOptionDTO[];
    disabled?: boolean;
    updateValidationStatus: (status: ValidationResult<UpdateBotToolSettingsRequest>) => void;
    getMessageWrapper: (f: string) => string;
}

const actionTypeOptions: SelectOption<ActionType>[] = [
    { label: "Exclude", value: ActionType.Exclude },
    { label: "Increase Price", value: ActionType.IncreasePrice },
    { label: "Reduce Price", value: ActionType.ReducePrice }
];

export const RuleSection: React.FC<RuleSectionProps> = ({
    section,
    sectionIndex,
    fieldOptions,
    disabled,
    updateValidationStatus,
    getMessageWrapper
}) => {
    const dispatch = useAppDispatch();

    const handleToggleMatch = (isAllMatch: boolean) => {
        dispatch(updateRuleSection({
            sectionIndex,
            section: { ...section, isAllMatch }
        }));
    };

    const handleActionTypeChange = (actionType: ActionType) => {
        const priceModifier = (actionType === ActionType.IncreasePrice || actionType === ActionType.ReducePrice)
            ? { subtractionValue: 0, subtractionType: SubtractionType.Number }
            : undefined;
        
        dispatch(updateRuleSection({
            sectionIndex,
            section: { 
                ...section, 
                valuationAction: {
                    ...section.valuationAction,
                    actionType,
                    priceModifier
                }
            }
        }));
    };

    const handleUpdateSubtractionValue = (value: string) => {
        const numValue = Number(value) || 0;
        dispatch(updateRuleSection({
            sectionIndex,
            section: {
                ...section,
                valuationAction: {
                    ...section.valuationAction,
                    priceModifier: {
                        ...section.valuationAction.priceModifier!,
                        subtractionValue: numValue
                    }
                }
            }
        }));
    };

    const handleUpdateSubtractionType = (type: SubtractionType) => {
        dispatch(updateRuleSection({
            sectionIndex,
            section: {
                ...section,
                valuationAction: {
                    ...section.valuationAction,
                    priceModifier: {
                        ...section.valuationAction.priceModifier!,
                        subtractionType: type
                    }
                }
            }
        }));
    };

    const handleAddConditionGroup = () => {
        dispatch(addConditionGroup(sectionIndex));
    };

    const handleDeleteSection = () => {
        dispatch(deleteRuleSection(sectionIndex));
    };

    const groups = section.conditionGroups || [];
    const actionType = section.valuationAction?.actionType;
    const showPriceModifier = actionType === ActionType.IncreasePrice || actionType === ActionType.ReducePrice;
    const title = actionType === ActionType.IncreasePrice ? "Increase Price" : "Reduce Price";

    const getPriceModifierError = (): string | undefined => {
        const rawError = getMessageWrapper('modifierRules');
        if (rawError) {
            try {
                const errorObject = JSON.parse(rawError);
                const sectionError = errorObject[`section_${section.id}`];
                return sectionError?.priceModifier;
            } catch (e) {
                return undefined;
            }
        }
        return undefined;
    };

    return (
        <div className={styles.section}>
            <div className={styles.header}>
                <div className={styles.headerControls}>
                    <CustomSwitchField
                        textOnTrue="AND"
                        textOnFalse="OR"
                        value={section.isAllMatch}
                        toggleValue={handleToggleMatch}
                        disabled={disabled}
                    />

                    <div className={styles.actionSelector}>
                        <span className={styles.label}>Action:</span>
                        <SelectDropdown
                            title=""
                            value={actionType}
                            options={actionTypeOptions}
                            handleChange={handleActionTypeChange}
                            disabled={disabled}
                            selectFirstOptionByDefault={false}
                        />
                    </div>

                    {showPriceModifier && (
                        <div className={styles.priceModifier}>
                            <span className={styles.label}>{title}:</span>
                            <SubtractInputField
                                type="number"
                                value={section.valuationAction.priceModifier?.subtractionValue}
                                setValue={handleUpdateSubtractionValue}
                                subtractionType={section.valuationAction.priceModifier?.subtractionType as SubtractionType}
                                onSubtractionChange={handleUpdateSubtractionType}
                                disabled={disabled}
                                placeholder="value"
                                title=""
                                errorMessage={getPriceModifierError()}
                            />
                        </div>
                    )}
                </div>

                <DeleteButton
                    disabled={disabled}
                    handleClick={handleDeleteSection}
                />
            </div>

            <div className={styles.groups}>
                {groups.map((group, groupIndex) => (
                    <React.Fragment key={groupIndex}>
                        <ConditionGroup
                            group={group}
                            groupIndex={groupIndex}
                            sectionIndex={sectionIndex}
                            fieldOptions={fieldOptions}
                            disabled={disabled}
                            updateValidationStatus={updateValidationStatus}
                            getMessageWrapper={getMessageWrapper}
                        />
                        {groupIndex < groups.length - 1 && (
                            <div className={styles.logicalOperator}>
                                {section.isAllMatch ? 'AND' : 'OR'}
                            </div>
                        )}
                    </React.Fragment>
                ))}

                <button
                    className={styles.addGroupButton}
                    onClick={handleAddConditionGroup}
                    disabled={disabled}
                >
                    + Add Condition Group
                </button>
            </div>
        </div>
    );
};

